import { useId, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useGetMtoReceiptSummaryQuery } from '@shared/api';
import { Button } from '@shared/ui/Button';
import { IconUpload } from '@shared/ui/icons';
import { Notice, TabPanel, Tabs, TextButton, typo, type TabItem } from '@entities/fleet';
import { useActionPermission } from '@entities/user';
import { ImportDrawer } from './ImportDrawer';
import { ReceiptObjects } from './ReceiptObjects';
import { ReceiptsJournal } from './ReceiptsJournal';
import { ReceiptsSummary } from './ReceiptsSummary';
import { FlashBox, Lead, PanelBox, Root, TabsRow } from './styles';

type TabId = 'journal' | 'summary' | 'objects';

const TABS: readonly TabItem<TabId>[] = [
  { id: 'journal', label: 'Журнал' },
  { id: 'summary', label: 'Свод по объектам' },
  { id: 'objects', label: 'Объекты прихода' },
];

function isTab(v: string | null): v is TabId {
  return v === 'journal' || v === 'summary' || v === 'objects';
}

/**
 * «Приход ТМЦ»: поступления материалов по счетам-фактурам с объектом. Один
 * экран у МТО (ведут) и у Экономиста (смотрят и сопоставляют объекты).
 * Вкладка и год — в адресе (`?tab=&year=`), чтобы ссылкой можно было
 * поделиться.
 */
export function ReceiptsPage({ context }: { context: 'mto' | 'econ' }) {
  const uid = useId();
  const [params, setParams] = useSearchParams();
  const tabParam = params.get('tab');
  const tab: TabId = isTab(tabParam) ? tabParam : 'journal';
  const yearParam = Number(params.get('year'));
  const { data: summary } = useGetMtoReceiptSummaryQuery(
    Number.isFinite(yearParam) && yearParam > 2000 ? { year: yearParam } : undefined,
  );
  const years = useMemo(() => summary?.years ?? [], [summary]);
  const year =
    Number.isFinite(yearParam) && yearParam > 2000
      ? yearParam
      : (summary?.year ?? new Date().getFullYear());
  const canWrite = useActionPermission('mto.receipts.write').allowed;
  const canMap = useActionPermission('mto.receipts.map').allowed;
  const [importOpen, setImportOpen] = useState(false);
  const [flash, setFlash] = useState<{ tone: 'success' | 'danger'; text: string } | null>(null);

  function setParam(patch: Record<string, string | null>) {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(patch)) {
          if (v === null || v === '') next.delete(k);
          else next.set(k, v);
        }
        return next;
      },
      { replace: true },
    );
  }

  const lead =
    context === 'econ'
      ? 'Поступления материалов по счетам-фактурам с объектом — как их разносит отдел МТО. ' +
        'Материалы в БДР берутся отсюда по месяцу поступления, без НДС: «по проекту» — в строку ' +
        '«Согласно проектной документации», остальное — в «Прочие материалы». На вкладке ' +
        '«Объекты прихода» видно, к какому объекту или подразделению относится каждое написание.'
      : 'Поступления материалов по счетам-фактурам: дата, поставщик, материал, сумма и объект. ' +
        'Журнал заменяет книгу Excel «Приход ТМЦ» — суммы здесь всегда числа, итоги по объектам ' +
        'считаются сами, а экономист берёт материалы в БДР отсюда.';

  return (
    <Root>
      <Lead>{typo(lead)}</Lead>

      {flash && (
        <FlashBox>
          <Notice $tone={flash.tone} role={flash.tone === 'danger' ? 'alert' : 'status'}>
            <span className="text">{typo(flash.text)}</span>
            <TextButton type="button" onClick={() => setFlash(null)}>
              Скрыть
            </TextButton>
          </Notice>
        </FlashBox>
      )}

      <TabsRow>
        <Tabs
          items={TABS}
          value={tab}
          onChange={(id) => setParam({ tab: id === 'journal' ? null : id })}
          idPrefix={uid}
          label="Разделы прихода"
        />
        <span className="spacer" />
        {canWrite && (
          <Button type="button" $variant="secondary" onClick={() => setImportOpen(true)}>
            <IconUpload size={16} />
            Загрузить книгу Excel
          </Button>
        )}
      </TabsRow>

      <PanelBox>
        <TabPanel idPrefix={uid} id={tab}>
          {tab === 'journal' && (
            <ReceiptsJournal
              year={year}
              years={years}
              canWrite={canWrite}
              onYear={(y) => setParam({ year: String(y) })}
              onFlash={setFlash}
              onImport={() => setImportOpen(true)}
            />
          )}
          {tab === 'summary' && (
            <ReceiptsSummary
              year={year}
              years={years}
              onYear={(y) => setParam({ year: String(y) })}
              onOpenObject={(objectId, month) =>
                setParam({
                  tab: null,
                  object: objectId,
                  month: month ? String(month) : null,
                })
              }
            />
          )}
          {tab === 'objects' && <ReceiptObjects year={year} canMap={canMap} onFlash={setFlash} />}
        </TabPanel>
      </PanelBox>

      {importOpen && (
        <ImportDrawer
          onClose={() => setImportOpen(false)}
          onDone={(text) => {
            setImportOpen(false);
            setFlash({ tone: 'success', text });
          }}
        />
      )}
    </Root>
  );
}
